import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { useDateLocale, useTimeZone } from '../../../hooks'
import Text from '../../Text'
import { useCalendarGridContext } from '../CalendarGrid.hooks'
import { CalendarGridCard } from '../CalendarGridCard'

import {
  QA_NAME_CALENDAR_GRID_DAY_POPOVER,
  QA_NAME_CALENDAR_GRID_DAY_POPOVER_EMPTY,
} from './CalendarGridDayPopover.constants'
import { useCalendarGridDayPopoverHandlers } from './CalendarGridDayPopover.hooks'
import {
  getCalendarGridDayPopoverCommonTagItem,
  getCalendarGridDayPopoverItems,
  getHasCalendarGridDayLoadSummary,
  getIsCalendarGridDayEmpty,
} from './CalendarGridDayPopover.utils'
import { CalendarGridDayPopoverCompactItem } from './CalendarGridDayPopoverCompactItem'
import { CalendarGridDayPopoverDate } from './CalendarGridDayPopoverDate'
import { CalendarGridDayPopoverMeta } from './CalendarGridDayPopoverMeta'
import { CalendarGridDayPopoverRow } from './CalendarGridDayPopoverRow'

import * as styles from './CalendarGridDayPopover.module.css'

function CalendarGridDayPopover({
  day,
  dayLoad,
  isDayLoadView,
  items,
  onClickItem,
  onClickSecondaryLabel,
  onClose,
}) {
  const { t } = useTranslation()

  const locale = useDateLocale()
  const timezone = useTimeZone()

  const { DayPopoverActions, ItemRow, ItemTag } = useCalendarGridContext()

  const { handleClickItem, handleClickSecondaryLabel } =
    useCalendarGridDayPopoverHandlers({
      onClickItem,
      onClickSecondaryLabel,
      onClose,
    })

  const Row = isDayLoadView
    ? (ItemRow ?? CalendarGridDayPopoverRow)
    : CalendarGridDayPopoverCompactItem

  const commonTagItem = ItemTag
    ? getCalendarGridDayPopoverCommonTagItem(items)
    : null

  const isTagInHeader =
    !isDayLoadView && !getHasCalendarGridDayLoadSummary(dayLoad)

  const headerTagItem = isTagInHeader ? commonTagItem : null

  const cardTagItem = isDayLoadView ? commonTagItem : headerTagItem

  return (
    <CalendarGridCard
      Actions={DayPopoverActions}
      Meta={CalendarGridDayPopoverMeta}
      actionsProps={{ day, dayLoad, items, onClose }}
      metaProps={{ dayLoad, headerTagItem, isDayLoadView }}
      qaName={QA_NAME_CALENDAR_GRID_DAY_POPOVER}
      onClose={onClose}
    >
      {isDayLoadView ? null : <CalendarGridDayPopoverDate day={day} />}
      <div
        className={classNames(styles.rows, {
          [styles.compact]: !isDayLoadView,
        })}
      >
        {getCalendarGridDayPopoverItems(items).map(item => (
          <Row
            isTagShown={!cardTagItem}
            item={item}
            key={item.id}
            locale={locale}
            timezone={timezone}
            onClickItem={handleClickItem}
            onClickSecondaryLabel={
              onClickSecondaryLabel && handleClickSecondaryLabel
            }
          />
        ))}
      </div>
      {getIsCalendarGridDayEmpty(items) ? (
        <Text isSecondary qaName={QA_NAME_CALENDAR_GRID_DAY_POPOVER_EMPTY}>
          {t('Calendar.EmptyDayKey')}
        </Text>
      ) : null}
      {isDayLoadView ? <CalendarGridDayPopoverDate day={day} /> : null}
    </CalendarGridCard>
  )
}

export default CalendarGridDayPopover
